import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { GamePlayerCore } from "../components/GamePlayer";
import {
  createRecoveryCode,
  finishTournamentRound,
  getTournamentHostTicket,
  getTournamentRun,
  prepareTournamentRound,
  startTournament,
} from "../api/api_funcs";
import TournamentHostTransport from "../tournament/TournamentHostTransport";
import { useRemotePage } from "../remote/RemoteControlContext";
import { buildTournamentControllerUrl } from "../tournament/inviteUrl";
import { canBeginRoundCountdown, parseTutorialRemoteCommand } from "../tournament/tutorialGate";
import { configuredLanguage } from "../tutorials/language";
import {
  addControllerToUnity,
  dispatchControllerInput,
  populateUnity,
  removeControllerFromUnity,
  resolveGameKey,
  startUnityRound,
} from "../tournament/gameRegistry";

export default function TournamentRunPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [connections, setConnections] = useState({});
  const [error, setError] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState({});
  const [recoveryControllerId, setRecoveryControllerId] = useState("");
  const transportRef = useRef(null);
  const unityRef = useRef(null);
  const runRef = useRef(null);
  const gameKeyRef = useRef(null);
  const sessionRef = useRef(null);
  const finishingRef = useRef(false);
  const disconnectTimersRef = useRef(new Map());
  const removedControllersRef = useRef(new Set());
  const countdownTimerRef = useRef(null);
  const countdownSessionRef = useRef(null);
  const unityReadySessionRef = useRef(null);
  const tutorialCompleteSessionRef = useRef(null);
  const startedSessionRef = useRef(null);

  useEffect(() => { runRef.current = run; }, [run]);
  const currentGame = run?.current_game;
  const gameKey = useMemo(() => resolveGameKey(currentGame), [currentGame]);
  useEffect(() => { gameKeyRef.current = gameKey; }, [gameKey]);
  useEffect(() => { sessionRef.current = sessionId; }, [sessionId]);

  const clearRoundCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    countdownSessionRef.current = null;
    setCountdown(null);
  }, []);

  useEffect(() => {
    if (countdownSessionRef.current && countdownSessionRef.current !== sessionId) clearRoundCountdown();
    if (unityReadySessionRef.current && unityReadySessionRef.current !== sessionId) unityReadySessionRef.current = null;
    if (tutorialCompleteSessionRef.current && tutorialCompleteSessionRef.current !== sessionId) tutorialCompleteSessionRef.current = null;
    if (startedSessionRef.current && startedSessionRef.current !== sessionId) startedSessionRef.current = null;
  }, [clearRoundCountdown, sessionId]);

  useEffect(() => () => clearRoundCountdown(), [clearRoundCountdown]);

  const beginCountdownIfReady = useCallback(() => {
    const activeSessionId = sessionRef.current;
    const instance = unityRef.current;
    const current = runRef.current;
    const key = gameKeyRef.current;
    if (!canBeginRoundCountdown({
      activeSessionId,
      unityReadySessionId: unityReadySessionRef.current,
      tutorialCompleteSessionId: tutorialCompleteSessionRef.current,
      startedSessionId: startedSessionRef.current,
      countdownSessionId: countdownSessionRef.current,
      hasUnityInstance: Boolean(instance),
      hasRun: Boolean(current),
      hasGameKey: Boolean(key),
    })) return;

    countdownSessionRef.current = activeSessionId;
    let remaining = Number(current.auto_start_delay_seconds || 0);
    setCountdown(remaining);

    const startRound = () => {
      if (sessionRef.current !== activeSessionId || startedSessionRef.current === activeSessionId) return;
      startedSessionRef.current = activeSessionId;
      countdownSessionRef.current = null;
      startUnityRound(instance, key);
      setCountdown(null);
    };

    if (remaining <= 0) {
      startRound();
      return;
    }

    countdownTimerRef.current = window.setInterval(() => {
      if (sessionRef.current !== activeSessionId) {
        clearRoundCountdown();
        return;
      }
      remaining -= 1;
      setCountdown(Math.max(0, remaining));
      if (remaining <= 0) {
        window.clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        startRound();
      }
    }, 1000);
  }, [clearRoundCountdown]);

  useEffect(() => {
    window.__ZAHOOR_TOURNAMENT_ACTIVE__ = true;
    window.__ZAHOOR_TOURNAMENT_SEND__ = (kind, rawPayload) => {
      const raw = String(rawPayload || "");
      let targetName;
      let remainder;
      if (kind === "mathrace") {
        try {
          const envelope = JSON.parse(raw);
          targetName = String(envelope.target || "");
          remainder = String(envelope.payload || "");
        } catch (parseError) {
          console.warn("Invalid MathRace tournament event", parseError);
          return;
        }
      } else {
        const separator = kind === "uno" || kind === "marbles" ? "|" : ",";
        const index = raw.indexOf(separator);
        targetName = index >= 0 ? raw.slice(0, index) : raw;
        remainder = index >= 0 ? raw.slice(index + 1) : "";
      }
      const roster = runRef.current?.roster;
      const participant = Object.values(roster?.participants || {}).find((item) => item.name === targetName);
      let payload = remainder;
      if (kind === "color") payload = `${gameKeyRef.current === "marble_sling" ? "Color|" : "Color,"}${remainder}`;
      if (kind === "field") payload = `FieldInfo|${remainder}`;
      if (kind === "loaded") payload = `Loaded|${remainder}`;
      if (kind === "marbles") payload = `Marbels|${remainder}`;
      if (kind === "playerInfo") payload = `Info,${remainder}`;
      const message = { type: "game.event", roundId: sessionId, payload };
      if (targetName === "*") transportRef.current?.broadcast(message);
      else if (participant?.controller_id) transportRef.current?.send(participant.controller_id, message);
    };
    return () => {
      delete window.__ZAHOOR_TOURNAMENT_SEND__;
      delete window.__ZAHOOR_TOURNAMENT_ACTIVE__;
    };
  }, [sessionId]);

  const refreshRun = useCallback(async () => {
    const data = await getTournamentRun(runId);
    setRun(data);
    return data;
  }, [runId]);

  useEffect(() => {
    refreshRun().catch((requestError) => setError(requestError.message));
  }, [refreshRun]);

  useEffect(() => {
    if (run?.status !== "lobby") return undefined;
    const timer = window.setInterval(() => refreshRun().catch(() => {}), 2000);
    return () => window.clearInterval(timer);
  }, [refreshRun, run?.status]);

  useEffect(() => {
    const disconnectTimers = disconnectTimersRef.current;
    const transport = new TournamentHostTransport({
      ticketProvider: async () => (await getTournamentHostTicket(runId)).signalingTicket,
      onConnectionChange: (controllerId, connected) => {
        setConnections((current) => ({ ...current, [controllerId]: connected }));
        const current = runRef.current;
        const existingTimer = disconnectTimers.get(controllerId);
        if (existingTimer) {
          window.clearTimeout(existingTimer);
          disconnectTimers.delete(controllerId);
        }
        if (!connected && current?.status === "playing") {
          const timer = window.setTimeout(() => {
            removeControllerFromUnity(unityRef.current, gameKeyRef.current, runRef.current?.roster, controllerId);
            removedControllersRef.current.add(controllerId);
            disconnectTimers.delete(controllerId);
          }, Number(current.reconnect_window_seconds || 0) * 1000);
          disconnectTimers.set(controllerId, timer);
        }
        if (connected && removedControllersRef.current.delete(controllerId)) {
          addControllerToUnity(unityRef.current, gameKeyRef.current, current?.roster, controllerId);
        }
        if (connected && current?.status === "playing" && sessionRef.current && gameKeyRef.current) {
          transportRef.current?.send(controllerId, {
            type: "round.prepare",
            roundId: sessionRef.current,
            gameKey: gameKeyRef.current,
            roster: current.roster,
          });
        }
      },
      onInput: (controllerId, message) => {
        if (message.type === "controller.ready") {
          setConnections((current) => ({ ...current, [controllerId]: true }));
          // MathRace sends its current question/score when the embedded calculator
          // finishes loading, so a zero-delay tournament start cannot lose that state.
          if (gameKeyRef.current === "math_race") {
            addControllerToUnity(
              unityRef.current,
              gameKeyRef.current,
              runRef.current?.roster,
              controllerId,
            );
          }
          return;
        }
        if (message.type !== "controller.input") return;
        if (message.roundId && String(message.roundId) !== String(sessionRef.current)) return;
        const tutorialAction = parseTutorialRemoteCommand(message.payload);
        if (tutorialAction) {
          window.dispatchEvent(new CustomEvent("onscreen:tutorial-control", {
            cancelable: true,
            detail: {
              action: tutorialAction,
              senderId: controllerId,
              roundId: sessionRef.current,
            },
          }));
          return;
        }
        const roster = runRef.current?.roster;
        const controller = roster?.controllers?.[controllerId];
        const participantId = message.participantId || controller?.participant_ids?.[0];
        const participant = roster?.participants?.[participantId];
        dispatchControllerInput(unityRef.current, gameKeyRef.current, participant, message.payload);
      },
    });
    transportRef.current = transport;
    transport.connect().catch((connectionError) => setError(connectionError.message));
    return () => {
      transport.close();
      for (const timer of disconnectTimers.values()) window.clearTimeout(timer);
      disconnectTimers.clear();
      if (transportRef.current === transport) transportRef.current = null;
    };
  }, [runId]);

  useEffect(() => {
    if (run?.status !== "playing" || sessionId || !currentGame) return;
    const storageKey = `tournament-active-session:${runId}`;
    const restart = Boolean(window.sessionStorage.getItem(storageKey));
    prepareTournamentRound(runId, restart)
      .then((data) => {
        setSessionId(data.sessionId);
        window.sessionStorage.setItem(storageKey, data.sessionId);
      })
      .catch((requestError) => setError(requestError?.response?.data?.detail || requestError.message));
  }, [currentGame, run?.status, runId, sessionId]);

  const handleUnityInstance = useCallback((instance) => {
    unityRef.current = instance;
  }, []);

  const handleUnityReady = useCallback((instance) => {
    const current = runRef.current;
    const key = gameKeyRef.current;
    if (!current || !key) return;
    removedControllersRef.current.clear();
    populateUnity(instance, key, current.roster);
    const roundMessage = {
      type: "round.prepare",
      roundId: sessionId,
      gameKey: key,
      roster: current.roster,
    };
    transportRef.current?.broadcast(roundMessage);
    unityReadySessionRef.current = sessionId;
    beginCountdownIfReady();
  }, [beginCountdownIfReady, sessionId]);

  const handleTutorialComplete = useCallback((completion) => {
    const completedSessionId = String(completion?.sessionId || "");
    if (!completedSessionId || completedSessionId !== String(sessionRef.current || "")) return;
    tutorialCompleteSessionRef.current = sessionRef.current;
    beginCountdownIfReady();
  }, [beginCountdownIfReady]);

  const handleGameFinished = useCallback(async (gameInfo) => {
    if (finishingRef.current || !sessionId) return;
    finishingRef.current = true;
    try {
      const result = await finishTournamentRound(runId, sessionId, gameInfo);
      transportRef.current?.broadcast({ type: "round.finished", roundId: sessionId });
      window.sessionStorage.removeItem(`tournament-active-session:${runId}`);
      clearRoundCountdown();
      setSessionId(null);
      setRun(result.run);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError.message);
    } finally {
      finishingRef.current = false;
    }
  }, [clearRoundCountdown, runId, sessionId]);

  async function beginTournament() {
    setError("");
    try {
      setRun(await startTournament(runId));
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError.message);
    }
  }

  async function issueRecoveryCode(controllerId) {
    try {
      const result = await createRecoveryCode(runId, controllerId);
      setRecoveryCodes((current) => ({ ...current, [controllerId]: result.recoveryCode }));
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || requestError.message);
    }
  }

  const remoteParticipants = Object.values(run?.roster?.participants || {});
  const remoteControllerIds = Object.keys(run?.roster?.controllers || {});
  const selectedRecoveryController = recoveryControllerId || remoteControllerIds[0] || "";
  const connectedCount = remoteControllerIds.filter((id) => connections[id]).length;

  useRemotePage({
    id: "tournament-run",
    title: run?.tournament?.name || "Tournament",
    subtitle: run?.status === "lobby" ? `${remoteParticipants.length} players in lobby` : run?.status === "finished" ? "Tournament finished" : currentGame?.title || "Preparing round",
    state: { status: run?.status || "loading", players: remoteParticipants.length, controllers: remoteControllerIds.length, connected: connectedCount, currentGame: currentGame?.title || "", countdown },
    sections: run?.status === "lobby" ? [
      { title: "Lobby", controls: [
        { id: "run.begin", type: "button", label: "Begin tournament", disabled: !remoteParticipants.length },
        { id: "run.recoveryController", type: "select", label: "Recovery device", value: selectedRecoveryController, options: remoteControllerIds.map((id, index) => ({ value: id, label: `Device ${index + 1}` })) },
        { id: "run.recovery", type: "button", label: "Show recovery code", disabled: !selectedRecoveryController },
      ] },
    ] : run?.status === "finished" ? [
      { title: "Tournament", controls: [{ id: "run.back", type: "button", label: "Back to tournaments" }] },
    ] : [],
    actions: {
      "run.begin": beginTournament,
      "run.recoveryController": ({ value }) => setRecoveryControllerId(String(value || "")),
      "run.recovery": () => selectedRecoveryController && issueRecoveryCode(selectedRecoveryController),
      "run.back": () => navigate("/tournaments"),
    },
  });

  if (!run) return <main className="flex-1 p-8">Loading tournament…</main>;
  const participants = Object.values(run.roster?.participants || {});
  const inviteUrl = buildTournamentControllerUrl(
    run.invite_token,
    process.env.REACT_APP_CONTROLLERS_SERVER,
    window.location.origin,
    configuredLanguage,
  );

  if (run.status === "finished") {
    return <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-10"><h1 className="text-3xl font-bold">Tournament finished</h1><div className="mt-6 space-y-2">{run.standings?.standings?.map((item, index) => <div key={item.id} className="flex rounded bg-slate-900 p-3"><span className="w-10">{index + 1}.</span><span className="flex-1">{item.name}</span><strong>{Math.round(item.score)}</strong></div>)}</div></main>;
  }

  if (run.status === "lobby") {
    return (
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 text-left">
        <h1 className="text-3xl font-bold">{run.tournament.name} lobby</h1>
        <div className="mt-6 grid gap-8 md:grid-cols-[260px_1fr]">
          <div className="rounded-xl bg-white p-5 text-center text-slate-900">{inviteUrl && <QRCode value={inviteUrl} size={210} />}<p className="mt-3 text-sm">Scan once for the whole tournament</p>{inviteUrl && <a className="mt-3 block break-all text-xs font-semibold text-indigo-700 underline" href={inviteUrl}>Open tournament controller</a>}</div>
          <section><h2 className="text-xl font-semibold">Players ({participants.length})</h2><div className="mt-3 space-y-2">{participants.map((player) => <div key={player.id} className="flex items-center rounded bg-slate-900 p-3"><span className="flex-1">{player.name}</span>{player.team && <span className="capitalize">{player.team}</span>}<span className={`ml-3 h-3 w-3 rounded-full ${connections[player.controller_id] ? "bg-green-400" : "bg-slate-500"}`} /></div>)}</div><div className="mt-4 space-y-2">{Object.keys(run.roster?.controllers || {}).map((controllerId, index) => <div key={controllerId} className="flex items-center gap-2 text-sm"><span>Device {index + 1}</span><button className="rounded border border-white/30 px-2 py-1" onClick={() => issueRecoveryCode(controllerId)}>New recovery code</button>{recoveryCodes[controllerId] && <strong>{recoveryCodes[controllerId]}</strong>}</div>)}</div><button className="mt-6 rounded bg-emerald-600 px-5 py-3 font-semibold disabled:opacity-50" disabled={!participants.length} onClick={beginTournament}>Begin tournament</button></section>
        </div>
        {error && <p className="mt-4 text-red-300">{error}</p>}
      </main>
    );
  }

  return (
    <main className="flex-1 px-4 py-6">
      <div className="mb-3 flex items-center gap-4"><h1 className="text-xl font-semibold">{run.tournament.name}: {currentGame?.title}</h1>{countdown !== null && <strong className="rounded bg-fuchsia-600 px-4 py-2">Starting in {countdown}</strong>}<details className="ml-auto"><summary>Reconnect device</summary>{Object.keys(run.roster?.controllers || {}).map((controllerId, index) => <div key={controllerId} className="mt-2 flex gap-2"><span>Device {index + 1}</span><button onClick={() => issueRecoveryCode(controllerId)}>Code</button>{recoveryCodes[controllerId] && <strong>{recoveryCodes[controllerId]}</strong>}</div>)}</details></div>
      {error && <p className="mb-3 text-red-300">{error}</p>}
      {sessionId && gameKey && <GamePlayerCore key={sessionId} gameKey={gameKey} externalSessionId={sessionId} tournamentMode onUnityInstance={handleUnityInstance} onReady={handleUnityReady} onGameFinished={handleGameFinished} onTutorialComplete={handleTutorialComplete} />}
    </main>
  );
}
